/**
 * Lykke on the left: the order's must-haves, the conversation, and a voice call. Answers come in by chat or voice
 * and are written to the order only when the user stated them; every write shows as "Wrote X" and jumps to it.
 */
import { useEffect, useRef, useState } from 'react';
import { Check, Phone, PhoneOff, SendHorizontal, Loader2 } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { MUST_HAVES, doneCount, type Chapter, type OrderState } from './orderState';

export type ThreadItem =
  | { kind: 'msg'; who: 'lykke' | 'user'; text: string }
  | { kind: 'wrote'; field: string; value: string; chapter: Chapter };

export function LykkeNavigator(props: {
  state: OrderState;
  thread: ThreadItem[];
  busy: boolean;
  call: 'off' | 'connecting' | 'live';
  callCaption: string;
  onSend: (text: string) => void;
  onCall: () => void;
  onJump: (ch: Chapter) => void;
}) {
  const { state, thread, busy, call, callCaption } = props;
  const [draft, setDraft] = useState('');
  const threadRef = useRef<HTMLDivElement>(null);
  useEffect(() => { threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' }); }, [thread.length, callCaption]);
  const done = doneCount(state);

  const send = () => { const t = draft.trim(); if (!t || busy) return; setDraft(''); props.onSend(t); };

  return (
    <aside aria-label="Lykke, your account manager" className="flex min-h-0 flex-col border-b border-gray-200 bg-white lg:h-full lg:w-[380px] lg:flex-none lg:border-b-0 lg:border-r">
      <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3">
        <span className={cn('relative grid h-9 w-9 flex-none place-items-center rounded-full bg-[#2E024C] text-sm text-white', call === 'live' && 'ring-2 ring-[#A9F600] ring-offset-2')} style={{ fontWeight: 700 }} aria-hidden>L</span>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-[#101828]" style={{ fontWeight: 600 }}>Lykke</div>
          <div className="text-xs text-[#667085]">{call === 'live' ? 'On a call with you' : call === 'connecting' ? 'Connecting' : 'Your account manager at Beatly'}</div>
        </div>
        <button
          type="button" onClick={props.onCall}
          className={cn('inline-flex h-9 items-center gap-1.5 rounded-full px-3.5 text-[13px]', call === 'off' ? 'bg-[#A9F600] text-[#14200A]' : 'bg-[#E5484D] text-white')}
          style={{ fontWeight: 700 }}
        >
          {call === 'connecting' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : call === 'live' ? <PhoneOff className="h-4 w-4" aria-hidden /> : <Phone className="h-4 w-4" aria-hidden />}
          {call === 'off' ? 'Call' : 'End call'}
        </button>
      </div>

      <div className="flex flex-col gap-2 border-b border-gray-200 px-4 py-3">
        <div className="flex items-baseline justify-between">
          <span className="text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>What we need to start</span>
          <span className="text-xs tabular-nums text-[#667085]">{done} of {MUST_HAVES.length}</span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-gray-100" role="progressbar" aria-valuemin={0} aria-valuemax={MUST_HAVES.length} aria-valuenow={done} aria-label="Must-haves done">
          <div className="h-full rounded-full bg-[#BE43FF] transition-all" style={{ width: `${(done / MUST_HAVES.length) * 100}%` }} />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {MUST_HAVES.map((m) => {
            const ok = m.done(state);
            return (
              <button key={m.id} type="button" onClick={() => props.onJump(m.chapter)}
                className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px]', ok ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-[#667085] hover:bg-gray-200')}>
                {ok && <Check className="h-3 w-3" aria-hidden />}{m.label}
              </button>
            );
          })}
        </div>
      </div>

      <div ref={threadRef} className="flex min-h-[200px] flex-1 flex-col gap-2.5 overflow-y-auto px-4 py-3 [scrollbar-width:none]" aria-live="polite">
        {thread.map((t, i) => t.kind === 'msg' ? (
          <p key={i} className={cn('max-w-[92%] rounded-2xl px-3 py-2 text-[13.5px] leading-relaxed', t.who === 'lykke' ? 'self-start rounded-bl-md bg-gray-100 text-[#101828]' : 'self-end rounded-br-md bg-[#BE43FF] text-white')}>{t.text}</p>
        ) : (
          <button key={i} type="button" onClick={() => props.onJump(t.chapter)} className="inline-flex items-start gap-1.5 self-start text-left text-xs text-emerald-700 hover:underline">
            <Check className="mt-px h-3.5 w-3.5 flex-none" aria-hidden /><span>Wrote <span className="text-[#101828]" style={{ fontWeight: 600 }}>{t.field}:</span> {t.value}</span>
          </button>
        ))}
        {busy && <p className="self-start rounded-2xl rounded-bl-md bg-gray-100 px-3 py-2 text-[13px] text-[#667085]"><Loader2 className="mr-1 inline h-3.5 w-3.5 animate-spin" aria-hidden />Lykke is writing</p>}
        {call === 'live' && callCaption && <p className="self-stretch rounded-xl border border-dashed border-gray-200 px-3 py-2 text-[13px] text-[#667085]">{callCaption}</p>}
      </div>

      <form className="flex items-end gap-2 border-t border-gray-200 px-4 py-3" onSubmit={(e) => { e.preventDefault(); send(); }}>
        <label htmlFor="lykke-say" className="sr-only">Message Lykke</label>
        <textarea
          id="lykke-say" value={draft} onChange={(e) => setDraft(e.target.value)} rows={1}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Talk or type to Lykke"
          className="max-h-32 min-h-[42px] flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#BE43FF]"
        />
        <button type="submit" disabled={!draft.trim() || busy} aria-label="Send" className="grid h-[42px] w-[42px] flex-none place-items-center rounded-xl bg-[#BE43FF] text-white disabled:opacity-40">
          <SendHorizontal className="h-4 w-4" aria-hidden />
        </button>
      </form>
    </aside>
  );
}
